import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { transitionTo } from "../Transition";
import { Counter } from "../Reveal";
import { BUSINESSES } from "../../data/businesses";

export function BusinessHero({ business }) {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".b-hero-line",
        { y: 0, yPercent: 110 },
        { y: 0, yPercent: 0, duration: 1.1, ease: "power4.out", stagger: 0.12, delay: 0.35 }
      );
      gsap.fromTo(
        ".b-hero-meta",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.9 }
      );
    });
    return () => ctx.revert();
  }, [business.slug]);

  return (
    <div className="relative flex h-[92svh] items-end overflow-hidden" data-testid="business-hero">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={business.hero}
          alt={business.name}
          className="h-[115%] w-full object-cover brightness-[0.65] contrast-[1.05]"
          data-parallax
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/40" />
      <div className="relative z-10 w-full px-[6vw] pb-14">
        <div className="overflow-hidden">
          <p className="b-hero-line font-mono text-[10px] uppercase tracking-[0.35em] text-amber md:text-xs">
            {business.category}
            {business.location ? ` — ${business.location}` : ""}
          </p>
        </div>
        <h1 className="mt-4">
          <span className="block overflow-hidden">
            <span className="b-hero-line block font-display text-[10vw] font-black uppercase leading-[0.9] tracking-tighter md:text-[6.5vw]">
              {business.name}
            </span>
          </span>
        </h1>
        <p className="b-hero-meta mt-6 max-w-xl text-sm leading-relaxed text-bone/80 md:text-lg">
          {business.tagline}
        </p>
      </div>
    </div>
  );
}

export function BusinessMetrics({ metrics }) {
  if (!metrics || metrics.length === 0) return null;
  return (
    <div
      className="grid grid-cols-1 gap-px border-y border-white/10 bg-white/10 md:grid-cols-3"
      data-testid="business-metrics"
    >
      {metrics.map((m, i) => (
        <div key={m.label} className="bg-ink px-[6vw] py-10 md:px-10 md:py-14" data-testid={`business-metric-${i}`}>
          <div className="font-display text-5xl font-black tracking-tight text-bone md:text-6xl">
            {m.raw ? m.raw : <Counter value={m.value} suffix={m.suffix || ""} />}
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-amber">
            {m.label}
          </p>
        </div>
      ))}
    </div>
  );
}

export function BusinessContent({ business }) {
  return (
    <div className="px-[6vw] py-20 md:py-28" data-testid="business-content">
      <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber" data-reveal>
        Overview
      </p>
      <p className="mt-8 max-w-3xl font-display text-2xl font-bold leading-snug tracking-tight text-bone md:text-4xl" data-reveal>
        {business.description.split(". ")[0]}.
      </p>
      <p className="mt-8 max-w-2xl text-sm leading-relaxed text-smoke md:text-base" data-reveal>
        {business.description}
      </p>

      {business.items && (
        <div className="mt-14 divide-y divide-white/10 border-y border-white/10" data-reveal>
          {business.items.map((item, i) => (
            <div key={item} className="flex items-baseline gap-5 py-4">
              <span className="font-mono text-[10px] text-amber">0{i + 1}</span>
              <p className="max-w-2xl text-sm leading-relaxed text-bone/80">{item}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-16 grid gap-12 md:grid-cols-2 md:gap-16">
        {business.sections.map((s) => (
          <div key={s.title} data-reveal>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight md:text-3xl">
              {s.title}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-smoke md:text-base">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BusinessGallery({ business }) {
  return (
    <div className="space-y-6 px-[6vw] pb-20" data-testid="business-gallery">
      {business.gallery.map((src, i) => (
        <div key={src} className="overflow-hidden" data-mask>
          <img
            src={src}
            alt={`${business.name} — image ${i + 1}`}
            className="h-[45vh] w-full object-cover brightness-[0.85] contrast-[1.05] md:h-[70vh]"
            loading="lazy"
            decoding="async"
          />
        </div>
      ))}
    </div>
  );
}

export function BusinessNavigation({ current }) {
  const navigate = useNavigate();
  const idx = BUSINESSES.findIndex((b) => b.slug === current.slug);
  const prev = BUSINESSES[(idx - 1 + BUSINESSES.length) % BUSINESSES.length];
  const next = BUSINESSES[(idx + 1) % BUSINESSES.length];
  const go = (e, href) => {
    e.preventDefault();
    transitionTo(navigate, href);
  };
  return (
    <div className="grid border-t border-white/10 md:grid-cols-2" data-testid="business-navigation">
      {[
        { b: prev, label: "Previous" },
        { b: next, label: "Next" },
      ].map(({ b, label }, i) => (
        <a
          key={label}
          href={`/businesses/${b.slug}`}
          onClick={(e) => go(e, `/businesses/${b.slug}`)}
          className={`group border-white/10 py-10 md:py-14 ${i === 0 ? "border-b px-[6vw] md:border-b-0 md:border-r" : "px-[6vw] text-right"}`}
          data-testid={`business-nav-${label.toLowerCase()}`}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
            {label} Business
          </p>
          <p className="mt-3 font-display text-3xl font-black uppercase tracking-tight transition-colors duration-300 group-hover:text-amber md:text-5xl">
            {b.shortName}
          </p>
        </a>
      ))}
    </div>
  );
}
